// v1.8 P0: 6 Incoterm 提示卡 — 与 docs/WORKFLOW.md § 2.12 对齐
// 表格内容: 卖方责任 / 买方责任 / 风险分界 / 适合场景 / 注意项
// 标识: alert-triangle 风险 / check-circle-2 责任清晰 / lightbulb 建议 / circle-alert 严重
// v1.9: icon 字段从 emoji 改为 lucide name (kebab-case)

export type IncotermKind = 'EXW' | 'FOB' | 'CFR' | 'CIF' | 'DAP' | 'DDP';

/** 注意项 icon 类型 — 用 lucide 名字符串 (kebab-case) */
export type IncotermNoteIcon = 'alert-triangle' | 'check-circle-2' | 'lightbulb' | 'circle-alert';

export interface IncotermInfo {
  code: IncotermKind;
  /** 中文全名 */
  fullNameZh: string;
  /** 英文全名 */
  fullNameEn: string;
  /** 卖方责任 (一句话) */
  sellerZh: string;
  /** 买方责任 (一句话) */
  buyerZh: string;
  /** 风险分界点 */
  riskTransferZh: string;
  /** 适合场景 */
  scenarioZh: string;
  /** 注意项 (3-5 条, 每条带 icon) */
  notes: Array<{ icon: IncotermNoteIcon; text: string }>;
}

export const INCOTERM_INFO: Record<IncotermKind, IncotermInfo> = {
  EXW: {
    code: 'EXW',
    fullNameZh: '工厂交货',
    fullNameEn: 'Ex Works',
    sellerZh: '工厂交货 (出厂前一切)',
    buyerZh: '全程运输 + 保险 + 出口报关 + 进口清关',
    riskTransferZh: '工厂门口 (卖方风险最小)',
    scenarioZh: '🇨🇳 国内小单 / 客户有自提能力 / 客户代办出口',
    notes: [
      { icon: 'check-circle-2', text: '卖方风险最小, 不办出口报关' },
      { icon: 'alert-triangle', text: '客户自办出口报关 = 复杂, 多数客户无此能力' },
      { icon: 'circle-alert', text: '建议款到再生产, 货出厂后无追款能力' },
    ],
  },
  FOB: {
    code: 'FOB',
    fullNameZh: '装运港船上交货',
    fullNameEn: 'Free On Board',
    sellerZh: '装船 + 出口报关 + 港杂',
    buyerZh: '海运 + 保险 + 进口清关',
    riskTransferZh: '货物越过船舷 (装船时)',
    scenarioZh: '🇨🇳 出口商最常用 / 责任清晰 / 货代可客户指定',
    notes: [
      { icon: 'check-circle-2', text: '中国出口商最常用, 责任清晰, 卖方风险可控' },
      { icon: 'alert-triangle', text: '货代如客户指定 → 出险卖方可能被追责, 必查货代资质' },
      { icon: 'lightbulb', text: '推荐场景: 客户有进口能力 + 信任度高' },
    ],
  },
  CFR: {
    code: 'CFR',
    fullNameZh: '成本加运费',
    fullNameEn: 'Cost and Freight',
    sellerZh: '海运 + 出口报关',
    buyerZh: '保险 + 进口清关 (卖方不办保险)',
    riskTransferZh: '装船 (与 FOB 相同)',
    scenarioZh: '客户无保险能力 / 客户指定货代',
    notes: [
      { icon: 'alert-triangle', text: '卖方必须投保 — 这是卖方责任, 不投保 = 海损卖方赔' },
      { icon: 'lightbulb', text: '如果客户要自己买保险 → 用 CFR 而不是 CIF' },
      { icon: 'check-circle-2', text: '卖方控制船期, 适合大件 / 散货' },
    ],
  },
  CIF: {
    code: 'CIF',
    fullNameZh: '成本+保险+运费',
    fullNameEn: 'Cost, Insurance and Freight',
    sellerZh: '海运 + 出口报关 + 保险 (110% 货值, ICC A 条款)',
    buyerZh: '进口清关 + 派送',
    riskTransferZh: '装船 (与 FOB 相同)',
    scenarioZh: '客户无外贸能力 / 一站式到港 / 中小客户首选',
    notes: [
      { icon: 'circle-alert', text: '卖方必须买保险 (110% 货值, ICC A 条款), 不买 = 客户可拒收' },
      { icon: 'alert-triangle', text: 'CIF 货值 ≥ USD 20,000 建议买出口信用保险 (中国信保)' },
      { icon: 'lightbulb', text: 'DDP 卖方风险大 → 只在信任客户时用' },
    ],
  },
  DAP: {
    code: 'DAP',
    fullNameZh: '目的地交货',
    fullNameEn: 'Delivered At Place',
    sellerZh: '送至指定目的地 (不含关税)',
    buyerZh: '进口清关 + 关税 + 末端派送',
    riskTransferZh: '指定目的地 (车上/仓库门口)',
    scenarioZh: '客户有清关能力 / 卖方无目的国资质',
    notes: [
      { icon: 'check-circle-2', text: '比 DDP 风险小 (卖方不付关税)' },
      { icon: 'alert-triangle', text: '卖方仍需安排跨国运输 + 末端, 物流链长' },
      { icon: 'lightbulb', text: '过渡方案: 客户有 VAT 号但不想自办运输' },
    ],
  },
  DDP: {
    code: 'DDP',
    fullNameZh: '完税后交货',
    fullNameEn: 'Delivered Duty Paid',
    sellerZh: '送至指定目的地 + 关税 + VAT + 末端派送',
    buyerZh: '接货 (零责任)',
    riskTransferZh: '指定目的地 (车上/客户仓库)',
    scenarioZh: '🇨🇳→🇺🇸 跨境电商 / 客户完全无外贸能力',
    notes: [
      { icon: 'circle-alert', text: '卖方风险最大, 关税/VAT 估错 = 利润归零甚至亏本' },
      { icon: 'circle-alert', text: '必须找目的国清关代理, 没本地代理 = 货卡海关 30+ 天' },
      { icon: 'alert-triangle', text: '只在信任客户 + 熟悉目的国关税 时用' },
      { icon: 'lightbulb', text: '适合 🇯🇵 日本 (0 关税) 比 🇺🇸 美国 (关税 + VAT) 风险小' },
    ],
  },
};

/** 取得某个 Incoterm 的提示卡 (容错: 大小写/无效) */
export function getIncotermInfo(code: string): IncotermInfo | null {
  const upper = code.toUpperCase() as IncotermKind;
  return INCOTERM_INFO[upper] ?? null;
}
